import type { DestinatarioFactura } from '@/features/clients/clients';
import { formatCents } from '@/features/catalog/catalog';

/**
 * A nombre de quién se emite la factura, y en qué proporción.
 *
 * El mismo editor sirve para la ficha del cliente —el reparto habitual— y para
 * un trabajo puntual, porque el problema es idéntico: dividir un importe entre
 * personas que suman cien.
 *
 * **No confundir con nada de lo económico.** Esto no reparte ganancia, no elige
 * quién factura de la organización, no asigna movilidad y no toca la plata que
 * recibe la veterinaria. Es sólo el nombre que va en el comprobante. La
 * auditoría de agosto dejó claro que son cinco dimensiones distintas y que
 * mezclarlas es lo que hace que una termine tapando a otra.
 */
export function RepartoFactura({
  lineas,
  onChange,
  totalCents,
  testId,
}: {
  lineas: DestinatarioFactura[];
  onChange: (l: DestinatarioFactura[]) => void;
  /** Si viene, se muestra cuánto le toca a cada uno. */
  totalCents?: number | null;
  testId?: string;
}) {
  const suma = lineas.reduce((a, l) => a + (Number(l.percentage) || 0), 0);
  // Con decimales, comparar contra 100 exacto rechaza repartos legítimos como
  // 33,33 / 33,33 / 33,34. Se compara con la misma tolerancia que usa la base.
  const completo = lineas.length === 0 || Math.abs(suma - 100) < 0.0001;

  const cambiar = (i: number, campo: 'name' | 'percentage', valor: string) => {
    const copia = lineas.map((l, j) =>
      j === i
        ? { ...l, [campo]: campo === 'percentage' ? Number(valor.replace(',', '.')) || 0 : valor }
        : l,
    );
    onChange(copia);
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId ?? 'reparto-factura'}>
      {lineas.length === 0 && (
        <p className="text-xs text-[var(--tinta-suave)]">
          Sin reparto: la factura se emite entera a nombre del cliente.
        </p>
      )}

      {lineas.map((l, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={l.name}
            onChange={(e) => cambiar(i, 'name', e.target.value)}
            placeholder="A nombre de"
            className="min-h-11 min-w-0 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
            data-testid="destinatario-nombre"
          />
          <input
            inputMode="decimal"
            value={String(l.percentage)}
            onChange={(e) => cambiar(i, 'percentage', e.target.value)}
            className="min-h-11 w-20 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-right text-base"
            data-testid="destinatario-pct"
          />
          <span className="text-sm opacity-60">%</span>
          {totalCents != null && (
            <span className="w-24 shrink-0 text-right text-sm tabular-nums opacity-70">
              {formatCents(Math.round((totalCents * (Number(l.percentage) || 0)) / 100))}
            </span>
          )}
          <button
            type="button"
            onClick={() => onChange(lineas.filter((_, j) => j !== i))}
            aria-label={`Quitar a ${l.name || 'este destinatario'}`}
            className="min-h-11 w-9 shrink-0 rounded-lg text-lg opacity-60"
            data-testid="destinatario-quitar"
          >
            ×
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() =>
            onChange([
              ...lineas,
              // El primero arranca en 100 porque el caso normal es uno solo; a
              // partir del segundo en 0, para que quien reparte escriba el
              // número que quiere y no corrija el que le pusieron.
              { name: '', percentage: lineas.length === 0 ? 100 : 0 },
            ])
          }
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-dashed px-3 text-sm"
          data-testid="destinatario-agregar"
        >
          ＋ Agregar destinatario
        </button>

        {lineas.length > 0 && (
          <span
            className={`text-sm tabular-nums ${completo ? 'opacity-60' : 'font-medium text-[var(--peligro)]'}`}
            data-testid="destinatario-suma"
          >
            {suma.toLocaleString('es-AR', { maximumFractionDigits: 4 })} %
            {!completo && ' · debe sumar 100'}
          </span>
        )}
      </div>
    </div>
  );
}
